import {
    CSSProperties,
    MouseEvent as ReactMouseEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import { createPortal } from "react-dom"
import { usePopper } from "react-popper"
import { useClickAway, useUnmount } from "react-use"
import cn from "classnames"

import { useControls } from "modules/Call/common/hooks"
import { getOrCreatePortalNode } from "modules/Call/common/utils"
import { Call, Endpoints, VoximplantSdk } from "modules/Call/sdk"
import { Analytics } from "utils/analytics"
import { useInputDevicesContext } from "modules/Call/InputDevices"
import { useIsTablet } from "modules/PsyProfile/common/utils"
import { VideoOutputSelector } from "modules/Call/components/VideoOutputSelector"
import { AudioInputSelector } from "modules/Call/components/AudioInputSelector"
import { SelectorTooltip } from "modules/Call/components/SelectorTooltip"
import {
    LeaveCallButton,
    LeaveCallConfirmationPopover,
} from "modules/Call/components/LeaveCallButton"
import { HelpMoreMenuWrapper } from "modules/Call/components/HelpMoreMenuWrapper"
import { ScreenShareButton, ScreenSharePopover } from "modules/Call/components/ScreenShareButton"

export function getMobileStyles(cssProperties: CSSProperties) {
    const transform = cssProperties?.transform
    const translateParsed = transform ? parseTranslate(transform.toString()) : undefined
    if (translateParsed) {
        return {
            left: "50%",
            transform: `translate(-50%,${translateParsed["y"]})`,
        }
    }
    const translate3dParsed = transform ? parseTranslate3d(transform.toString()) : undefined
    if (translate3dParsed) {
        return {
            left: "50%",
            transform: `translate3d(-50%,${translate3dParsed["y"]},${translate3dParsed["z"]})`,
        }
    }
}

function parseTranslate(s: string) {
    const match = s.match(/translate\((.+),(.+)\)/)
    if (!match) return
    return { x: match[1], y: match[2] }
}

function parseTranslate3d(s: string) {
    const match = s.match(/translate3d\((.+),(.+),(.+)\)/)
    if (!match) return
    return { x: match[1], y: match[2], z: match[3] }
}

interface Props {
    isOpenedHelpModal: boolean
    classNames?: string
    call?: Call
    endpoints?: Endpoints
    isPsychotherapyMode?: boolean
    hangUpLink?: string
    lobbyLink?: string
    showVideo?: boolean
    toggleIsOpenedHelpModal: () => void
    onShowVideo?: () => void
    onScreenShareEnabled?: (on: boolean) => void
    setIsPsychotherapyMode?: () => void
}

export function VideoControls({
    isOpenedHelpModal,
    classNames,
    call,
    endpoints,
    isPsychotherapyMode,
    hangUpLink,
    lobbyLink,
    toggleIsOpenedHelpModal,
    onScreenShareEnabled,
    setIsPsychotherapyMode,
}: Props) {
    const rootRef = useRef<HTMLDivElement>(null)
    const [
        {
            cameras,
            microphones,
            camera,
            microphone,
            cameraEnabled,
            microphoneEnabled,
            cameraLoading,
            microphoneLoading,
            microphoneAccessed,
            cameraAccessed,
        },
    ] = useInputDevicesContext()
    const [
        { videoLoading, showScreenShareLoader, screenShareSupported, screenShareEnabled },
        { setMicrophone, setCamera, toggleCamera, toggleMicrophone, runToggleScreenShare },
    ] = useControls(call)
    const isTablet = useIsTablet()
    const screenShareRef = useRef<HTMLButtonElement>(null)
    const audioSelectorRef = useRef<HTMLButtonElement>(null)
    const videoSelectorRef = useRef<HTMLButtonElement>(null)
    const leaveCallButtonRef = useRef<HTMLButtonElement>(null)
    const [referenceElement, setReferenceElement] = useState<
        HTMLButtonElement | HTMLDivElement | null
    >(null)

    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)

    function setCameraPopup() {
        if (!cameras?.length) return
        setReferenceElement(videoSelectorRef.current)
        if (call) {
            Analytics.call_camera_change_opened()
        } else {
            Analytics.lobby_camera_change_opened()
        }
    }

    function setMicrophonePopup() {
        if (!microphones?.length) return
        setReferenceElement(audioSelectorRef.current)
        if (call) {
            Analytics.call_mic_change_opened()
        } else {
            Analytics.lobby_mic_change_opened()
        }
    }

    function setLeaveCallPopup() {
        setReferenceElement(leaveCallButtonRef.current)
        Analytics.call_end_call_opened()
    }

    function shareScreen() {
        runToggleScreenShare().catch(() => {
            setReferenceElement(screenShareRef.current)
        })
    }

    function hidePopup() {
        setReferenceElement(null)
    }

    const offset = useMemo(() => {
        if (referenceElement === screenShareRef.current) {
            return [124, 28] as [number, number]
        }
        if (referenceElement === audioSelectorRef.current) {
            return [-114, 28] as [number, number]
        }
        if (referenceElement === videoSelectorRef.current) {
            return [94, 28] as [number, number]
        }
        if (referenceElement === leaveCallButtonRef.current) {
            return [-114, 8] as [number, number]
        }
    }, [referenceElement])
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: "top",
        modifiers: [
            {
                name: "offset",
                options: {
                    offset,
                },
            },
        ],
    })
    const overrideStyles = !isTablet && styles.popper ? getMobileStyles(styles.popper) : undefined
    const portalNode = getOrCreatePortalNode()
    useUnmount(() => portalNode.remove())
    const tooltipProps = useMemo(() => {
        switch (referenceElement) {
            case audioSelectorRef.current:
                return {
                    selectedId: microphone?.id,
                    items: microphones,
                    handleSelect: setMicrophone,
                }
            case videoSelectorRef.current:
                return {
                    selectedId: camera?.id,
                    items: cameras,
                    handleSelect: setCamera,
                    locked: !camera?.id,
                }
            default:
                return {
                    selectedId: undefined,
                    items: undefined,
                    handleSelect: undefined,
                }
        }
    }, [referenceElement, microphones, cameras, microphone, camera])

    useClickAway(rootRef, (event) => {
        if (event.target instanceof Node && !portalNode.contains(event.target)) {
            hidePopup()
        }
    })

    const {
        screenShareShown,
        videoSelectorShown,
        audioSelectorShown,
        leaveCallConfirmationPopoverShown,
    } = useMemo(
        () => ({
            screenShareShown:
                referenceElement === screenShareRef.current && referenceElement != null,
            videoSelectorShown:
                referenceElement === videoSelectorRef.current && referenceElement != null,
            audioSelectorShown:
                referenceElement === audioSelectorRef.current && referenceElement != null,
            leaveCallConfirmationPopoverShown:
                referenceElement === leaveCallButtonRef.current && referenceElement != null,
        }),
        [referenceElement]
    )

    const handleClickControl = (event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!(event.target instanceof Node)) return
        if (portalNode.contains(event.target)) return
        if (screenShareRef.current?.contains(event.target) && !screenShareShown) {
            shareScreen()
        } else if (videoSelectorRef.current?.contains(event.target) && !videoSelectorShown) {
            setCameraPopup()
        } else if (audioSelectorRef.current?.contains(event.target) && !audioSelectorShown) {
            setMicrophonePopup()
        } else if (
            leaveCallButtonRef.current?.contains(event.target) &&
            !leaveCallConfirmationPopoverShown
        ) {
            setLeaveCallPopup()
        } else {
            hidePopup()
        }
    }

    const handleClickLinkLeave = () => {
        Analytics.call_end_call_selected()
    }

    useEffect(() => {
        onScreenShareEnabled?.(screenShareEnabled)
    }, [screenShareEnabled])

    const isShowScreenShare =
        screenShareSupported &&
        endpoints &&
        Object.values(endpoints).length > 0 &&
        !Object.values(endpoints).some(
            (endpoint) => endpoint?.kind === VoximplantSdk.MediaRendererKind.Sharing
        )

    return (
        <div
            ref={rootRef}
            className={cn("video-controls", classNames, {
                "is-detect-idle": !referenceElement,
            })}
            onClick={handleClickControl}
        >
            {call && (
                <HelpMoreMenuWrapper
                    isOpenedHelpModal={isOpenedHelpModal}
                    onClickMoreButton={(isOpen, tooltipRef) =>
                        isOpen ? setReferenceElement(tooltipRef) : setReferenceElement(null)
                    }
                    isPsychotherapyMode={isPsychotherapyMode}
                    setIsPsychotherapyMode={setIsPsychotherapyMode}
                    toggleIsOpenedHelpModal={toggleIsOpenedHelpModal}
                />
            )}
            {isShowScreenShare && (
                <ScreenShareButton
                    isHovered={screenShareShown}
                    isEnabled={screenShareEnabled}
                    ref={screenShareRef}
                    lockToggle={showScreenShareLoader}
                />
            )}
            {call &&
                screenShareShown &&
                createPortal(
                    <ScreenSharePopover
                        ref={setPopperElement}
                        onClose={() => setReferenceElement(null)}
                        style={{ ...styles.popper, ...overrideStyles }}
                        {...attributes.popper}
                    />,
                    portalNode
                )}
            <VideoOutputSelector
                ref={videoSelectorRef}
                toggleVideo={() => void toggleCamera()}
                videoOff={!cameraEnabled || !cameraAccessed}
                lockVideoToggle={videoLoading || cameraLoading}
                opened={videoSelectorShown}
            />
            <AudioInputSelector
                ref={audioSelectorRef}
                toggleAudio={() => toggleMicrophone()}
                audioOff={!microphoneEnabled || !microphoneAccessed}
                lockAudioToggle={microphoneLoading}
                opened={audioSelectorShown}
            />
            {(videoSelectorShown || audioSelectorShown) &&
                createPortal(
                    <SelectorTooltip
                        ref={setPopperElement}
                        selectedId={tooltipProps.selectedId}
                        items={tooltipProps.items}
                        handleSelect={tooltipProps.handleSelect}
                        locked={videoLoading}
                        style={{ ...styles.popper, ...overrideStyles }}
                        {...attributes.popper}
                    />,
                    portalNode
                )}
            {call && (
                <LeaveCallButton
                    className={cn({
                        "is-hovered": leaveCallConfirmationPopoverShown,
                    })}
                    ref={leaveCallButtonRef}
                />
            )}
            {call &&
                leaveCallConfirmationPopoverShown &&
                createPortal(
                    <LeaveCallConfirmationPopover
                        ref={setPopperElement}
                        hangUpLink={hangUpLink}
                        lobbyLink={lobbyLink}
                        onClick={handleClickLinkLeave}
                        style={{ ...styles.popper, ...overrideStyles }}
                        {...attributes.popper}
                    />,
                    portalNode
                )}
        </div>
    )
}
